import axios from 'axios';
import { useAuthStore } from '../store/index.js';

const API = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar token
API.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => API.post('/auth/login', { email, password }),
  register: (email, password, fullName, phone) => API.post('/auth/register', { email, password, fullName, phone }),
  getProfile: () => API.get('/auth/profile'),
  changeMyPassword: (currentPassword, newPassword) =>
    API.patch('/auth/change-password', { currentPassword, newPassword })
};

export const traccarAPI = {
  getServers: () => API.get('/traccar'),
  createServer: (name, url, username, password) => 
    API.post('/traccar', { name, url, username, password }),
  updateServer: (id, name, url, username, password) =>
    API.put(`/traccar/${id}`, { name, url, username, password }),
  deleteServer: (id) => API.delete(`/traccar/${id}`),
  testConnection: (id) => API.post(`/traccar/${id}/test`),
  getDevices: (id) => API.get(`/traccar/${id}/devices`)
};

export const templateAPI = {
  getTemplates: () => API.get('/templates'),
  inspectSourceFields: (payload) => API.post('/templates/source-fields', payload),
  getExecutionSummariesBatch: (templateIds) =>
    API.get('/templates/execution-summaries', { params: { ids: templateIds.join(',') } }),
  getExecutionSummary: (id) => API.get(`/templates/${id}/execution-summary`),
  clearTemplateLogs: (id) => API.delete(`/templates/${id}/logs`),
  createDefaultIntegrations: (integration = 'all') => API.post('/templates/bootstrap-defaults', { integration }),
  previewTemplate: (id, sampleData = {}, useLiveData = false, traccarBaseUrl = null) =>
    API.post(`/templates/${id}/preview`, { sampleData, useLiveData, traccarBaseUrl }),
  testTemplate: (id, sampleData = {}, useLiveData = false, traccarBaseUrl = null) =>
    API.post(`/templates/${id}/test`, { sampleData, useLiveData, traccarBaseUrl }),
  previewTemplateDraft: (templateDraft, sampleData = {}, useLiveData = false, traccarBaseUrl = null) =>
    API.post('/templates/preview-draft', { templateDraft, sampleData, useLiveData, traccarBaseUrl }),
  testTemplateDraft: (templateDraft, sampleData = {}, useLiveData = false, traccarBaseUrl = null) =>
    API.post('/templates/test-draft', { templateDraft, sampleData, useLiveData, traccarBaseUrl }),
  createTemplate: (data) => API.post('/templates', data),
  updateTemplate: (id, data) => API.put(`/templates/${id}`, data),
  transferTemplateOwner: (id, targetUserId) => API.patch(`/templates/${id}/owner`, { targetUserId }),
  shareTemplate: (id, userIds = []) => API.post(`/templates/${id}/share`, { userIds }),
  deleteTemplate: (id) => API.delete(`/templates/${id}`),
  getTemplate: (id) => API.get(`/templates/${id}`)
};

export const shipmentAPI = {
  getShipments: (limit = 100, offset = 0, startDate = null) => 
    API.get('/shipments', { params: { limit, offset, ...(startDate ? { startDate } : {}) } }),
  getTemplateStats: (startDate = null) =>
    API.get('/shipments/template-stats', { params: { ...(startDate ? { startDate } : {}) } }),
  createShipment: (templateId, deviceData = null) =>
    API.post('/shipments', { templateId, deviceData }),
  deleteAllShipments: () => API.delete('/shipments'),
  getShipment: (id) => API.get(`/shipments/${id}`),
  deleteShipment: (id) => API.delete(`/shipments/${id}`)
};

export const logAPI = {
  getLogs: (filters = {}, limit = 100, offset = 0, options = {}) =>
    API.get('/logs', { params: { limit, offset, ...filters, ...options } }),
  deleteAllLogs: () => API.delete('/logs'),
  getShipmentLogs: (shipmentId) =>
    API.get(`/logs/shipment/${shipmentId}`)
};

export const planAPI = {
  getPlans: () => API.get('/plans'),
  updatePlan: (planKey, data) => API.put(`/plans/${planKey}`, data),
  getSettings: () => API.get('/plans/settings'),
  updateSettings: (data) => API.put('/plans/settings', data)
};

export const userAPI = {
  getUsers: () => API.get('/users'),
  createUser: async (email, password, fullName, isAdmin = false, phone = '') => {
    try {
      return await API.post('/users', { email, password, fullName, isAdmin, phone });
    } catch (error) {
      if (error.response?.status === 404) {
        // Backward compatibility when backend was not restarted with /users POST route.
        return API.post('/auth/register', { email, password, fullName, phone });
      }
      throw error;
    }
  },
  updateUser: (id, email, fullName, isAdmin, phone = '') => API.patch(`/users/${id}`, { email, fullName, isAdmin, phone }),
  updateUserPermissions: (id, permissions) => API.patch(`/users/${id}/permissions`, permissions),
  updateUserPlan: (id, plan) => API.patch(`/users/${id}/plan`, { plan }),
  updateUserStatus: (id, isActive) => API.patch(`/users/${id}/status`, { isActive }),
  deleteUser: (id) => API.delete(`/users/${id}`),
  getUserTemplates: (id) => API.get(`/users/${id}/templates`),
  impersonateUser: (id) => API.post(`/users/${id}/impersonate`),
  resetUserPassword: (id, newPassword) => API.patch(`/users/${id}/password`, { newPassword }),
  getUserPassword: (id) => API.get(`/users/${id}/password`),
  getUserIntegrations: (id) => API.get(`/users/${id}/integrations`),
  assignUserIntegrations: (id, integrationTemplateIds = []) => API.post(`/users/${id}/integrations`, { integrationTemplateIds }),
  clearUserIntegrations: (id) => API.delete(`/users/${id}/integrations`),
  removeUserIntegration: (id, integrationId) => API.delete(`/users/${id}/integrations/${integrationId}`)
};

export default API;
